// Rich scrollytelling narrative for individual case-study detail pages.
// Standardised 8-part schema across all published studies:
//   01 Context · 02 Starting situation · 03 Scale & constraints · 04 Actions & systems
//   05 Measured result · 06 Attribution & evidence · 07 Sustainability · 08 Related service
// Each chapter: { n, title, body, image?, imageAlt?, link? }.
// The optional `link` renders as a call-to-action button beneath the chapter body.

export const CASE_STUDY_NARRATIVES = {
  "carpark-to-8000": {
    hero: {
      eyebrow: "Flagship · Student experience · Systems",
      headline: "From a folding table",
      accent: "to 8,000+ weekly students.",
      image: "/assets/umsu-events-team.webp",
      imageAlt: "Food-relief team and volunteers at the University of Melbourne",
    },
    metrics: [
      { value: "8,000+", label: "Weekly students supported" },
      { value: "400+", label: "Rotating volunteers" },
      { value: "20×", label: "Growth in participation" },
      { value: "12 mo", label: "Solo → full team" },
    ],
    quote: {
      body: "Most leaders measure themselves by how essential they are in the room. I measure myself by how quickly the room stops needing me.",
      attribution: "JJ Argent",
    },
    chapters: [
      {
        n: "01", title: "Context",
        body: "University of Melbourne, mid-pandemic. Students were facing a compounding cost-of-living crisis. UMSU had a small emergency food-relief function running out of a carpark — a dignified idea with no operating spine behind it.",
      },
      {
        n: "02", title: "Starting situation",
        body: "One coordinator. Roughly 400 weekly registrations. A folding table, a spreadsheet, and a rotating handful of volunteers. All institutional knowledge lived in one person's head.",
        image: "/assets/umsu-orientation-festival.webp",
        imageAlt: "COVID-era operations — masked volunteers packing food-relief kits",
      },
      {
        n: "03", title: "Scale & constraints",
        body: "Registrations climbed 5× in the first six months. Supplier logistics, volunteer scheduling, hardship triage, referrals and grant reporting all landed on the same coordinator. If that person got sick, the program stopped.",
      },
      {
        n: "04", title: "Actions & systems",
        body: "Redesigned the model as dignified retail — Union Mart. Authored SOPs and video training. Built volunteer rota, CRM and supplier registers. Introduced a referral and hardship-triage flow with Student Services. Documented every workflow so the next coordinator could pick it up on day one.",
        image: "/assets/umsu-orientation-week.webp",
        imageAlt: "Union Mart — the documented dignified-retail model",
      },
      {
        n: "05", title: "Measured result",
        body: "8,000+ students supported weekly. 400+ rotating volunteers. 20× growth in participation. Program kept running through weeks when the founding coordinator was unavailable — the operational-continuity test the system had been built for.",
      },
      {
        n: "06", title: "Attribution & evidence",
        body: "The dignified-retail logic began earlier at JJ's Community Support in North Melbourne — a community pantry backed by local press and coordinated with Melbourne Metro YHA. The pattern that scaled to 8,000+ students is the same one the newspaper covered.",
        image: "/assets/jjs-community-support-3.jpg",
        imageAlt: "Local newspaper article — JJ's Community Support calling out to locals to back its community pantry",
      },
      {
        n: "07", title: "Sustainability",
        body: "The SOPs, supplier registers, hardship-triage flow and volunteer manual were handed over to full-time UMSU staff. The program has continued to operate at scale after JJ moved on — the definition of built-to-run-without-me.",
      },
      {
        n: "08", title: "Related service",
        body: "This is the operating pattern behind the Program Resilience Audit and the Program & Experience Design engagements — mapping single-point-of-failure risk, documenting what lives in people's heads and rebuilding the workflow so the program outlasts any one coordinator.",
        link: { href: "/services#resilience-audit", label: "See the Resilience Audit" },
      },
    ],
    closing: {
      lead: "This is the case study behind the signature curve.",
      cta: "Have a program that shouldn't rely on one person?",
    },
  },

  "calgary-pride": {
    hero: {
      eyebrow: "Major events · Canada",
      headline: "Directing a $1.75M festival",
      accent: "for 145,000+ people.",
      // Placeholder: branded OG card until a real Calgary Pride photo is uploaded.
      image: `${process.env.REACT_APP_BACKEND_URL}/api/og/case-studies/calgary-pride`,
      imageAlt: "Calgary Pride — programming, production, community",
    },
    metrics: [
      { value: "145,000+", label: "Attendees" },
      { value: "24", label: "Events across 2 days" },
      { value: "$1.75M", label: "Budget stewardship" },
      { value: "230+", label: "Volunteers commanded" },
    ],
    quote: {
      body: "The best moment isn't opening night — it's the Monday after, when the team has slept, the receipts reconcile, and the community says 'do it again next year'.",
      attribution: "JJ Argent",
    },
    chapters: [
      {
        n: "01", title: "Context",
        body: "Calgary Pride returning to full-scale operations in a post-pandemic city. Board mandate: deliver the largest festival since 2019 without compromising safety, community trust or fiscal discipline.",
      },
      {
        n: "02", title: "Starting situation",
        body: "Skeleton core team, dormant supplier relationships, a partial permit map and an event schedule that had grown organically since 2022. No single production plan connected the 24 events in the calendar.",
      },
      {
        n: "03", title: "Scale & constraints",
        body: "$1.75M operating budget. 24-event schedule across two peak days plus wraparound programming. 9 core staff, 40–45 contractors, 230+ volunteers. Full permit and health-and-safety compliance non-negotiable across parade, park takeover and licensed venues.",
      },
      {
        n: "04", title: "Actions & systems",
        body: "Built a single production plan and event-day RACI. Multi-stage technical layouts, licensed footprints, vendor and sponsor maps, radio comms plan and incident-response playbook. Weekly cross-functional stand-ups with police, ambulance, city permits, finance and advocacy groups.",
      },
      {
        n: "05", title: "Measured result",
        body: "145,000+ attendees across the festival. Zero permit failures. 100% health-and-safety compliance. $1.75M budget delivered within tolerance. Community goodwill re-anchored after a difficult stretch.",
      },
      {
        n: "06", title: "Attribution & evidence",
        body: "Festival results published in Calgary Pride's 2024 annual report. Permit sign-offs held by City of Calgary. References available on request from the Calgary Pride Executive Director and the H&S consultancy engaged for the parade route.",
      },
      {
        n: "07", title: "Sustainability",
        body: "Left behind a repeatable production template, a supplier register, a volunteer manual and a documented event-day RACI — so the next festival lead could pick up the operating rhythm on day one.",
      },
      {
        n: "08", title: "Related service",
        body: "This is the operating pattern behind the Event Operating System Build engagement — production plan, permits, RACI and incident-response playbook wired into a single, transferable system.",
        link: { href: "/services#event-os", label: "See the Event OS Build" },
      },
    ],
    closing: {
      lead: "Delivery, diplomacy, dignity — at festival scale.",
      cta: "Planning a large-scale, high-stakes event?",
    },
  },

  "toronto-eventpro": {
    hero: {
      eyebrow: "University operations · Systems · Canada",
      headline: "17 buildings.",
      accent: "94 people. $2.8M budget.",
      image: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/8vaibz4r_Vic%20uni%20Emmaniuel%20College%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.webp",
      imageAlt: "Emmanuel College, Victoria University at U of Toronto — one of the 17 buildings in JJ's ancillary services portfolio",
    },
    metrics: [
      { value: "17", label: "Buildings" },
      { value: "94", label: "FTE unionised team" },
      { value: "$2.8M", label: "Operational budget" },
      { value: "100%", label: "H&S compliance" },
    ],
    quote: {
      body: "Institutional-scale operations don't fail because of talent. They fail because knowledge lives in inboxes.",
      attribution: "JJ Argent",
    },
    chapters: [
      {
        n: "01", title: "Context",
        body: "University of Toronto ancillary services — a 17-building precinct spanning graduations, guest-lecture series, the Toronto International Festival of Authors and daily campus operations. Predictable staff turnover was already an operational risk.",
        image: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/0x5ub275_Vic%20uni%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.jpg",
        imageAlt: "Victoria University campus building — part of JJ's 17-building portfolio",
      },
      {
        n: "02", title: "Starting situation",
        body: "A 94 FTE unionised team, a $2.8M operational budget, and workflows scattered across email threads, tribal memory and legacy tools. Cross-campus coordination happened in parallel silos: IT, security, facilities, catering, Dean of Students.",
        image: "/assets/uoft-victoria-staff.jpg",
        imageAlt: "Victoria University staff team",
      },
      {
        n: "03", title: "Scale & constraints",
        body: "Unionised workforce (with all the change-management discipline that implies). 24/7 on-call readiness. Zero-tolerance H&S environment. Any change had to be deliverable inside existing collective-agreement terms.",
        image: "/assets/uoft-victoria-security-team.webp",
        imageAlt: "Victoria University security team — part of the 24/7 on-call operation",
      },
      {
        n: "04", title: "Actions & systems",
        body: "Configured Eventpro to the institutional context. Authored SOPs and video training manuals for staff and student associations. Introduced automated reporting so leaders steered from data, not anecdote. Chaired weekly cross-campus operations meetings — IT, security, facilities, catering, Dean of Students — turning silos into a single decision cadence.",
        image: "/assets/uoft-eventpro-training.jpg",
        imageAlt: "Eventpro implementation training session",
      },
      {
        n: "05", title: "Measured result",
        body: "100% H&S compliance across the portfolio. Toronto International Festival of Authors delivered end-to-end. Cross-campus decisions consolidated into one weekly meeting cadence. Reference from the Business Manager available on request.",
        image: "/assets/uoft-toronto-black-film-festival.jpg",
        imageAlt: "Toronto Black Film Festival event, delivered with the Victoria University team",
      },
      {
        n: "06", title: "Attribution & evidence",
        body: "Business Manager reference contactable directly. SOP library, video training and weekly reporting artefacts available under NDA. Union-side references available with prior notice.",
        image: "/assets/uoft-victoria-events-team-dinner.jpg",
        imageAlt: "Victoria University events team dinner",
      },
      {
        n: "07", title: "Sustainability",
        body: "SOP library and video training remain in use. Weekly operations cadence continues under the next Business Manager. The system was designed to survive the person who built it — and it has.",
        image: "/assets/uoft-victoria-residential-team.jpg",
        imageAlt: "Victoria University residential team — the operation continues after handover",
      },
      {
        n: "08", title: "Related service",
        body: "This is the operating pattern behind the Workflow & AI Readiness Review and the Interim Program & Operations Leadership engagements — mapping institutional knowledge, codifying workflows, and installing a single decision cadence.",
        link: { href: "/services#ai-readiness", label: "See the AI Readiness Review" },
      },
    ],
    closing: {
      lead: "Ancillary services, rebuilt to run without a hero.",
      cta: "Inheriting a complex operation?",
    },
    portfolio: {
      eyebrow: "Portfolio of buildings",
      headline: "Some of the 17 buildings",
      accent: "I stewarded.",
      intro: "A snapshot of the Victoria College precinct at the University of Toronto — the physical scale behind the 94-FTE ancillary services operation.",
      items: [
        {
          src: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/8vaibz4r_Vic%20uni%20Emmaniuel%20College%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.webp",
          caption: "Emmanuel College",
          meta: "Victoria College · U of T",
          alt: "Emmanuel College building at Victoria University, U of Toronto",
        },
        {
          src: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/yngy8mf0_Vic%20uni%20dining%20hall%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.jpg",
          caption: "Ned's Café dining hall",
          meta: "Victoria College · U of T",
          alt: "Victoria College dining hall interior",
        },
        {
          src: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/123o5tnk_Vic%20uni%20Isabell%20Bader%20theatre%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.jpg",
          caption: "Isabel Bader Theatre",
          meta: "Victoria College · U of T",
          alt: "Isabel Bader Theatre auditorium",
        },
        {
          src: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/cicywm7s_Vic%20uni%20Burwash%20Residence%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.jpg",
          caption: "Burwash Residence",
          meta: "Victoria College · U of T",
          alt: "Burwash Residence building",
        },
        {
          src: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/0x5ub275_Vic%20uni%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.jpg",
          caption: "Old Vic (grounds)",
          meta: "Victoria College · U of T",
          alt: "Old Vic building and grounds",
        },
      ],
    },
  },

  "orientation-scale": {
    hero: {
      eyebrow: "Major events · University operations",
      headline: "Orientation Week",
      accent: "at institutional scale.",
      image: "/assets/umsu-events-team.webp",
      imageAlt: "University of Melbourne — Orientation Week programming and vendor stalls",
    },
    metrics: [
      { value: "145K+", label: "Annual stakeholders" },
      { value: "200+", label: "Vendor & club stalls" },
      { value: "$1.2M", label: "Sponsorship value" },
      { value: "9", label: "Buildings activated" },
    ],
    quote: {
      body: "Orientation is the first system a student meets. If it feels held-together, they'll assume the rest of the university is too.",
      attribution: "JJ Argent",
    },
    chapters: [
      {
        n: "01", title: "Context",
        body: "University of Melbourne Union — Events, Sponsorship & Advertising Division. Orientation Week is the flagship activation of the academic year, setting the tone for tens of thousands of returning and first-year students.",
      },
      {
        n: "02", title: "Starting situation",
        body: "Historically fragmented programming across five faculties and three campuses. Vendor and club stalls onboarded via ad-hoc email. Sponsorship agreements renewed by phone, not tracked in a central register. No single run-sheet connecting the 9 activated buildings.",
      },
      {
        n: "03", title: "Scale & constraints",
        body: "5-day activation across 9 buildings, 2 theatres and an amphitheatre. 200+ vendor and club stalls to onboard, orient and sign off. 145,000+ annual stakeholder engagements to plan for. Union governance model and student-safety framework applied throughout.",
      },
      {
        n: "04", title: "Actions & systems",
        body: "Introduced a single onboarding pipeline for stalls, a live master run-sheet, a sponsorship register with renewal cadence, and a modular event-template library that other divisional events could plug into. Cross-checked with campus security, WHS and comms weekly in the lead-up.",
      },
      {
        n: "05", title: "Measured result",
        body: "$1.2M sponsorship value secured across the division — the underlying 200% year-on-year sponsorship growth that anchors the impact-metrics band on the homepage. 200+ stalls delivered without permit or safety incident. +35% attendee-satisfaction lift measured post-event.",
      },
      {
        n: "06", title: "Attribution & evidence",
        body: "Sponsorship figures and event outcomes reported to the UMSU Board. Referees available: UMSU CEO and Head of Events. Vendor and sponsor references available on request.",
      },
      {
        n: "07", title: "Sustainability",
        body: "The modular event template, sponsorship register and onboarding pipeline were handed over to the incoming Events team. Orientation continues to run to the same rhythm — the operating system outlasted the coordinator.",
      },
      {
        n: "08", title: "Related service",
        body: "This is the operating pattern behind the Event Operating System Build and the Program & Experience Design engagements — a repeatable rhythm that any successor can pick up.",
        link: { href: "/services#event-os", label: "See the Event OS Build" },
      },
    ],
    closing: {
      lead: "Orientation, delivered as a system — not a scramble.",
      cta: "Running a major recurring campus event?",
    },
  },
};
